import { useMemo } from 'react';

interface ArrowsLayerProps {
    arrows?: string[][]; // [['e2', 'e4']]
    orientation: 'white' | 'black';
    tone?: 'default' | 'subtle';
}

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const RANKS = ['1', '2', '3', '4', '5', '6', '7', '8'];

export function ArrowsLayer({ arrows, orientation, tone = 'default' }: ArrowsLayerProps) {
    if (!arrows || arrows.length === 0) return null;

    const isSubtle = tone === 'subtle';
    const markerId = isSubtle ? 'arrowhead-annotation-subtle' : 'arrowhead-annotation';
    const markerSize = isSubtle ? 3.4 : 4;
    const markerRef = isSubtle ? 1.7 : 2;
    const strokeColor = isSubtle
        ? 'var(--color-board-annotation-subtle, rgba(255, 86, 35, 0.62))'
        : 'var(--color-board-annotation, var(--color-primary, #ffaa00))';
    const strokeWidth = isSubtle ? 1.1 : 1.5;
    const strokeOpacity = isSubtle ? 0.72 : 0.9;

    const lines = useMemo(() => {
        return arrows.map(([start, end], i) => {
            if (!start || !end) return null;
            const startCoords = getSquareCenter(start, orientation);
            const endCoords = getSquareCenter(end, orientation);

            // Calculate vector
            const dx = endCoords.x - startCoords.x;
            const dy = endCoords.y - startCoords.y;
            const length = Math.sqrt(dx * dx + dy * dy);

            // Shorten by 35% of a square width on both ends to avoid covering pieces
            // Square width is 12.5%
            const OFFSET = 12.5 * 0.35;

            // If arrow is shorter than 2x offset, don't draw (or just draw small dot?)
            if (length < OFFSET * 2) return null;

            const ux = dx / length;
            const uy = dy / length;

            return {
                x1: startCoords.x + ux * OFFSET,
                y1: startCoords.y + uy * OFFSET,
                x2: endCoords.x - ux * OFFSET,
                y2: endCoords.y - uy * OFFSET,
                key: `${start}-${end}-${i}`
            };
        }).filter(Boolean);
    }, [arrows, orientation]);

    return (
        <svg
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: isSubtle ? 7 : 10
            }}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
        >
            <defs>
                <marker
                    id={markerId}
                    markerWidth={markerSize}
                    markerHeight={markerSize}
                    refX={markerRef}
                    refY={markerRef}
                    orient="auto"
                >
                    <path d="M0,0 L4,2 L0,4" fill={strokeColor} />
                </marker>
            </defs>
            {lines.map(line => line && (
                <line
                    key={line.key}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    markerEnd={`url(#${markerId})`}
                    strokeLinecap="round"
                    opacity={strokeOpacity}
                />
            ))}
        </svg>
    );
}

function getSquareCenter(square: string, orientation: 'white' | 'black') {
    const file = square.charAt(0);
    const rank = square.charAt(1);

    const fileIdx = FILES.indexOf(file);
    const rankIdx = RANKS.indexOf(rank);

    if (fileIdx === -1 || rankIdx === -1) {
        return { x: 50, y: 50 }; // Fallback to center if invalid
    }

    // Standard White: a=0, 8=0.
    // coordinate x = fileIdx
    // coordinate y = 7 - rankIdx

    // Standard Black: a=7, 1=0.
    // coordinate x = 7 - fileIdx
    // coordinate y = rankIdx

    let x, y;
    if (orientation === 'white') {
        x = fileIdx;
        y = 7 - rankIdx;
    } else {
        x = 7 - fileIdx;
        y = rankIdx;
    }

    // Convert to percentage center (0-100)
    // 12.5 width per square. Center is +6.25
    return {
        x: (x * 12.5) + 6.25,
        y: (y * 12.5) + 6.25
    };
}
